import { fetchAsset } from "../core/AssetSource.js";
import { DEFAULT_FABRIC_CODE } from "../utils/constants.js";
import { runWhenIdle } from "../utils/idle.js";
import { assetUrl } from "../config.js";

/** The bottom bar's fabric swatch grid, filtered and ordered for the current selection. */
export class FabricGrid {
  constructor(app, uiManager) {
    this.app = app;
    this.ui = uiManager;
  }

  /** Forget the rendered fabric set, so the next updateMaterialGrid rebuilds. */
  invalidate() {
    this._lastMaterialFingerprint = null;
  }

  getAllowedFabricGroupsForSelection() {
    const furnitureManager = this.app.getFurnitureManager();
    const materialManager = this.app.getMaterialManager();
    if (!furnitureManager || !materialManager) return null;

    const selectedFurniture = furnitureManager.getSelectedFurnitureArray();
    if (selectedFurniture.length === 0) return null;

    // For multi-select, get intersection of allowed groups
    let allowedGroups = null;

    for (const furniture of selectedFurniture) {
      const urlCode = furniture.furnitureData?.urlCode;
      if (!urlCode) continue;

      const furnitureAllowed = materialManager.getAllowedFabricGroups(urlCode);

      if (furnitureAllowed) {
        if (allowedGroups === null) {
          allowedGroups = [...furnitureAllowed];
        } else {
          // Intersection - only keep groups that are in both
          allowedGroups = allowedGroups.filter(g => furnitureAllowed.includes(g));
        }
      }
    }

    return allowedGroups;
  }

  getOrderedFabricCodesForSelection() {
    const furnitureManager = this.app.getFurnitureManager();
    const materialManager = this.app.getMaterialManager();
    if (!furnitureManager || !materialManager) return null;

    const selectedFurniture = furnitureManager.getSelectedFurnitureArray();
    if (selectedFurniture.length === 0) return null;

    // Use the first selected furniture's urlCode for ordering
    for (const furniture of selectedFurniture) {
      const urlCode = furniture.furnitureData?.urlCode;
      if (!urlCode) continue;

      const orderedCodes = materialManager.getOrderedFabricCodes(urlCode);
      if (orderedCodes) return orderedCodes;
    }

    return null;
  }

  async updateMaterialGrid() {
    const materialManager = this.app.getMaterialManager();
    if (!materialManager) return;

    const materialGrid = document.getElementById("material-grid");
    if (!materialGrid) return;

    // Load the fabric sprite manifest once so swatches render from a single image.
    await this._ensureFabricSprite();

    // Get all fabrics from all allowed groups in one flat array
    const allowedGroups = this.getAllowedFabricGroupsForSelection();
    let fabrics;
    if (allowedGroups) {
      fabrics = allowedGroups.flatMap(groupKey => materialManager.getFabricsInGroup(groupKey));
    } else {
      // Fallback: get fabrics from all groups
      const fabricGroups = materialManager.getFabricGroups();
      fabrics = Object.keys(fabricGroups).flatMap(groupKey => materialManager.getFabricsInGroup(groupKey));
    }

    // Sort fabrics by product-colors order if available
    const orderedCodes = this.getOrderedFabricCodesForSelection();
    if (orderedCodes) {
      fabrics = [...fabrics].sort((a, b) => {
        const indexA = orderedCodes.indexOf(a.fabricCode);
        const indexB = orderedCodes.indexOf(b.fabricCode);
        // Fabrics not in the ordered list go to the end
        const orderA = indexA === -1 ? Infinity : indexA;
        const orderB = indexB === -1 ? Infinity : indexB;
        return orderA - orderB;
      });
      // Filter out fabrics not in the ordered list
      fabrics = fabrics.filter(f => orderedCodes.includes(f.fabricCode));
    }

    // Build a fingerprint to skip DOM rebuild when the same fabrics apply
    const fabricIds = fabrics.map(f => f.id);
    const fingerprint = fabricIds.join(',');
    if (fingerprint === this._lastMaterialFingerprint) {
      // Same fabrics — just update selection highlight
      this.updateMaterialSelection({ scroll: true });
      return;
    }
    this._lastMaterialFingerprint = fingerprint;

    // Set default selected material to ET-14 if none selected
    // Skip for multi-select so no swatch is highlighted
    const furnitureManager = this.app.getFurnitureManager();
    const isMulti = furnitureManager?.hasMultipleSelected();
    if (!this.ui.selectedMaterial && !isMulti) {
      const defaultFabricId = materialManager.getFabricIdByCode(DEFAULT_FABRIC_CODE);
      if (defaultFabricId) {
        this.ui.selectedMaterial = defaultFabricId;
      } else if (fabrics.length > 0) {
        this.ui.selectedMaterial = fabrics[0].id;
      }
    }

    // Build DOM using fragment to minimize reflows
    const fragment = document.createDocumentFragment();

    let previousGroup = null;
    for (const fabric of fabrics) {
      // Vertical rule between runs of different fabric groups
      if (previousGroup !== null && fabric.group !== previousGroup) {
        const divider = document.createElement("div");
        divider.className = "material-group-divider";
        fragment.appendChild(divider);
      }
      previousGroup = fabric.group;

      const item = document.createElement("div");
      item.className = `material-item ${fabric.id === this.ui.selectedMaterial ? "selected" : ""}`;
      item.dataset.materialId = fabric.id;

      // Create thumbnail
      this.createMaterialThumbnail(item, fabric, materialManager);

      // Add tooltip
      const fabricCode = fabric.fabricCode || fabric.id;
      item.title = `${fabricCode} - ${fabric.name} - $${fabric.price}`;

      item.addEventListener("click", () => {
        this.ui.selectMaterial(fabric.id);
      });

      fragment.appendChild(item);
    }

    materialGrid.innerHTML = "";
    materialGrid.appendChild(fragment);
    this.ui._scrollBarTo('.material-item.selected', 'center');
    this.ui._updateBarScrollButtons();

    // Warm the 512 maps for the shown fabrics on idle (only reached on a new fabric set).
    this.scheduleFabricTexturePrefetch(fabricIds);
  }

  /** Mark this.ui.selectedMaterial's swatch, optionally scrolling the bar to it. */
  updateMaterialSelection({ scroll = false } = {}) {
    const materialGrid = document.getElementById("material-grid");
    if (!materialGrid) return;
    for (const item of materialGrid.querySelectorAll(".material-item")) {
      item.classList.toggle("selected", item.dataset.materialId === this.ui.selectedMaterial);
    }
    if (scroll) this.ui._scrollBarTo('.material-item.selected', 'center');
  }

  // Load the sprite manifest once; sets this._fabricSprite ({url,cols,rows,index}) or null (fallback to per-code jpgs).
  _ensureFabricSprite() {
    if (this._fabricSpritePromise) return this._fabricSpritePromise;
    this._fabricSpritePromise = (async () => {
      try {
        const res = await fetchAsset("thumbnails/fabrics-sprite.json");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const m = await res.json();
        this._fabricSprite = {
          url: assetUrl("thumbnails/fabrics-sprite.jpg"),
          cols: m.cols,
          rows: m.rows,
          index: new Map(m.codes.map((code, i) => [code, i])),
        };
      } catch {
        this._fabricSprite = null;
      }
    })();
    return this._fabricSpritePromise;
  }

  createMaterialThumbnail(item, fabric, materialManager) {
    const code = fabric.fabricCode;
    if (!code) {
      this._appendTextureThumbnail(item, fabric, materialManager);
      return;
    }
    // Prefer the single sprite sheet: the whole grid is one HTTP request, positioned per
    // swatch with CSS. Falls back to the per-code jpg, then the texture-derived thumbnail.
    const sprite = this._fabricSprite;
    if (sprite && sprite.index.has(code)) {
      this._appendSpriteThumbnail(item, fabric, sprite, sprite.index.get(code));
      return;
    }
    this._appendThumbnailContent(item, fabric, assetUrl(`thumbnails/fabrics/${code}.jpg`));
    const img = item.querySelector("img.material-thumbnail-image");
    if (img) {
      img.loading = "lazy";
      img.decoding = "async";
      img.addEventListener("error", () => {
        item.innerHTML = "";
        this._appendTextureThumbnail(item, fabric, materialManager);
      }, { once: true });
    }
  }

  _appendSpriteThumbnail(item, fabric, sprite, i) {
    const { cols, rows, url } = sprite;
    const col = i % cols;
    const row = Math.floor(i / cols);
    const swatch = document.createElement("div");
    swatch.className = "material-thumbnail-image material-thumbnail-sprite";
    swatch.style.backgroundImage = `url("${url}")`;
    // Scale the sheet so one cell fills the swatch, then position with the classic
    // percentage-sprite formula (resolution-independent; handles non-square swatches).
    swatch.style.backgroundSize = `${cols * 100}% ${rows * 100}%`;
    swatch.style.backgroundPosition =
      `${cols > 1 ? (col / (cols - 1)) * 100 : 0}% ${rows > 1 ? (row / (rows - 1)) * 100 : 0}%`;
    item.appendChild(swatch);
    this._appendThumbnailLabels(item, fabric);
  }

  _appendTextureThumbnail(item, fabric, materialManager) {
    const thumbnailData = materialManager.generateThumbnail(fabric.id, 80);
    const hasRealTexture = !!materialManager.thumbnailCache[`${fabric.id}_80_tex`];

    this._appendThumbnailContent(item, fabric, thumbnailData);

    // Only load texture async if we don't already have the real thumbnail
    if (!hasRealTexture) {
      materialManager
        .getFabricData(fabric.id)
        .then((fabricData) => {
          if (fabricData && fabricData.texture) {
            const updatedThumbnail = materialManager.createTextureBasedThumbnail(
              fabricData.texture,
              80,
              fabric.id,
            );
            if (updatedThumbnail) {
              item.innerHTML = "";
              this._appendThumbnailContent(item, fabric, updatedThumbnail);
            }
          }
        })
        .catch((error) => {
          console.warn(`Failed to load texture for ${fabric.id}:`, error);
        });
    }
  }

  _appendThumbnailContent(item, fabric, thumbnailData) {
    if (thumbnailData) {
      const img = document.createElement("img");
      img.src = thumbnailData;
      img.alt = fabric.name;
      img.className = "material-thumbnail-image";
      item.appendChild(img);
    } else {
      const placeholder = document.createElement("div");
      placeholder.className = "material-loading-placeholder";
      placeholder.textContent = this.ui.t('designerLoadingMaterial', 'Loading...');
      item.appendChild(placeholder);
      return;
    }

    this._appendThumbnailLabels(item, fabric);
  }

  _appendThumbnailLabels(item, fabric) {
    const nameLabel = document.createElement("div");
    nameLabel.className = "material-name";
    nameLabel.textContent = fabric.name;
    item.appendChild(nameLabel);

    const codeLabel = document.createElement("div");
    codeLabel.className = "material-code";
    codeLabel.textContent = fabric.fabricCode || fabric.id;
    item.appendChild(codeLabel);
  }

  // Warm the 512 maps (diffuse + normal) for every fabric shown in the bar on idle so switching is
  // instant — cache-only (no THREE textures / `_pendingLoads`, so the 1k upgrade isn't blocked),
  // deduped per URL in MaterialManager, and skipped under Save-Data. `new Image()` is cheap and the
  // browser throttles the actual fetches, so no chunking is needed.
  scheduleFabricTexturePrefetch(fabricIds) {
    if (!fabricIds?.length) return;
    if (navigator.connection?.saveData) return; // respect the user's data-saver preference
    const mm = this.app.getMaterialManager?.();
    if (!mm?.prefetchFabricMaps) return;

    runWhenIdle(() => {
      for (const id of fabricIds) {
        mm.prefetchFabricMaps(id, { resolution: "512", maps: ["diffuse", "normal"] });
      }
    }, { timeout: 4000, fallbackDelay: 400 });
  }
}
